import type { QueryClient } from '@tanstack/react-query';
import { myCalendarsBaseKey } from '@lib/cache/events';
import {
  questionByUuidBaseKey,
  questionMySpacesFeedKey,
  resolveQuestionBaseKey,
  userQuestionsBaseKey,
} from '@lib/cache/questions';
import {
  communitiesKey,
  communityDetailKey,
  spaceEventsKey,
  spaceMembersKey,
  spacePartnersKey,
  spacesMyJoinedBaseKey,
} from '@lib/cache/spaces';

/**
 * What the caller knows about the membership its request may have changed. Three mutually
 * exclusive facts, as a discriminated union rather than the nullable id list this started as.
 *
 * `changedSpaceIds: string[] | null` carried all three meanings and let the DANGEROUS one collapse
 * into the SAFEST-LOOKING one: a caller writing `results?.map(...) ?? []` silently turned "I cannot
 * tell" into "provably nothing", the single input that sweeps nothing at all. Both correctness bugs
 * found on this helper's first review were that collapse happening for real. Here there is no way
 * to spell "unknown" that looks like an empty list of changes — `{ kind: 'unknown', candidateIds:
 * [] }` still fires every global sweep, because "I don't know which spaces" is not "no spaces".
 */
export type SpaceMembershipChange =
  /**
   * Provably nothing moved. The strongest claim available and the only one that skips the sweep
   * entirely: these are the broadest roots in the app (the whole community list, every cached
   * community detail, every my-joined variant, the combined calendar), so firing them for a
   * request that touched no membership row is pure waste.
   *
   * The union exists so a reviewer can audit who claims this variant, which only works while the
   * list below is complete. Re-derive it with `rg "kind: 'none'" src` rather than trusting it;
   * today it is five sites across three files:
   * - `useToggleGoingEvent` un-going — the DELETE removes an RSVP and never leaves a space, in
   *   any direction and after any number of attempts.
   * - `useToggleGoingEvent` going, when the response's `joined` + `pending` + `pin_required` are
   *   all empty, every `pin_required` item was readable, AND the invocation did not retry. The
   *   retry qualifier is load-bearing: `skipped` also means "already a member", so a retried
   *   attend can report an empty `joined + pending` for a space attempt 1 genuinely joined.
   * - `executePostAuthIntent` on the same empty-buckets shape, where the retry qualifier is
   *   satisfied by construction (one direct axios call, no React Query retry).
   * - `useShareQuestionToSpaces` when the server rejected the share before its one commit
   *   (`isPreWriteRejection`), so nothing it staged, auto-joins included, was kept.
   * - `useShareQuestionToSpaces` when the readable per-space rows contain no candidate at all
   *   (every row held, gated or errored, or `results: []`; or, un-retried, already there).
   */
  | { kind: 'none' }
  /**
   * Provably these spaces and no others. The rosters take exactly `ids`; a space the caller did
   * not name keeps its cached roster.
   */
  | { kind: 'these'; ids: string[] }
  /**
   * Cannot tell. An errored POST may still have committed (a timeout after commit looks exactly
   * like a rejection), and a server response can be unreadable or too coarse to prove either way.
   * `candidateIds` are the spaces worth sweeping rosters for — the best guess, not a claim — and
   * an EMPTY list is legitimate: the global surfaces still fire, since a caller that cannot name
   * a single space has not thereby ruled out a change.
   */
  | { kind: 'unknown'; candidateIds: string[] };

/**
 * Keys this helper would otherwise sweep that the CALLER already covers on the same settle.
 * Every field is required rather than optional so a new caller has to answer the question
 * instead of inheriting a silent wasted round-trip.
 *
 * Duplication is not free. Invalidating a key the caller already invalidated does NOT dedupe: for
 * an already-loaded query, `refetchQueries`' default `cancelRefetch: true` cancels the caller's
 * in-flight request and starts another (query-core 5.101.4 `queryClient.js:168`, `query.js:188-192`
 * — the `return this.#retryer.promise` dedupe branch is reachable only while `data === undefined`).
 * The first response is discarded, and a request without a `signal` still runs to completion
 * server-side.
 */
export interface SpaceMembershipSweptByCaller {
  /** Caller sweeps `eventsBaseKey()`, a prefix of every My Calendars and live-rail key. */
  eventRoot: boolean;
  /**
   * Caller sweeps `spaceEventsKey(id)` for its own reason, over a set that COVERS every space it
   * hands this helper. `spaceEventsKey` is a standalone root (`['spaceEvents', id, ...]`), reached
   * by no other key here, so setting this true while sweeping a narrower set silently drops the
   * event list of any space the caller left out.
   *
   * COVERS is a real requirement, not a hope: the one caller that sets this true
   * (`invalidateGoingEventQueries`) satisfies it by DERIVING its per-space set as the union of its
   * own space ids and this input's ids, rather than by arguing that the two happen to line up. A
   * new caller that cannot do the same must pass false and let this helper sweep the key.
   */
  spaceEventLists: boolean;
  /**
   * Caller sweeps `questionMySpacesFeedKey()` unconditionally for its own reason. The attend
   * pipeline does (a Going flip moves the "at {event}" chip) and so does the share pipeline (the
   * shared question itself lands in the feed). The explicit-join pipelines do not, so they pass
   * false and this helper sweeps it: a newly joined space's questions belong in the my-spaces feed.
   */
  mySpacesFeed: boolean;
  /**
   * Caller sweeps the post pages for its own reason, over a set that COVERS every post page its
   * request can have changed: `resolveQuestionBaseKey()` (slug routes) and `questionByUuidKey` of
   * each post it touched (uuid routes). This helper then skips its own two post-page roots, which
   * it sweeps for the viewer's HELD posts (see the end of the function). The own-profile list is
   * swept either way.
   *
   * COVERS has a second half here: the caller's request must be unable to move a held post the
   * caller did not name, or the pages of posts it never named go stale. Other posts' held links
   * move only when a membership leaves PENDING. The share pipeline is the one caller that sets
   * this true, and it qualifies on both halves: it sweeps the pages of the post it shared, which
   * is the only post whose links it writes (a pending sharer's Space answers `held`: a held link
   * of THAT post on their own request, TECH-683), and it never moves a membership out of
   * pending: the backend joins the sharer only where they had no membership row at all. Every
   * pipeline that can take a membership out of pending (join, leave, an accepted invitation, the
   * server echo) passes false.
   */
  heldPostPages: boolean;
}

/** The whole input: what moved, and what the caller has already covered. */
export interface SpaceMembershipSweep {
  change: SpaceMembershipChange;
  alreadySweptByCaller: SpaceMembershipSweptByCaller;
}

/**
 * Refreshes every cache a change to the viewer's space membership can move. The single definition
 * shared by every pipeline that joins or leaves an existing space (`useCreateCommunity` keeps its
 * own three-key sweep because nothing else is cached for a brand-new id):
 * - the two where the SERVER decides the membership as a side effect and the client only learns
 *   about it from the response: `invalidateGoingEventQueries` (the attend POST auto-joins the
 *   event's spaces) and `useShareQuestionToSpaces` (sharing a question into a frictionless space
 *   the asker has no membership in auto-joins it);
 * - the explicit-join pipelines: `useCommunityMembership` (join/leave from detail and event
 *   surfaces), `useSpaceCardJoin` (join from a card) and `invalidateAcceptedInvitationQueries`
 *   (accepting an invite, in-app and post-auth).
 *
 * They used to hand-roll lists that drifted apart (#1054, #1057); one definition means a key added
 * here reaches every caller, including `invalidateMembershipQueries`' self tier (the SSE echo and
 * the pending-membership poll).
 *
 * The predicate is narrow on purpose, and `{ kind: 'none' }` is the only input that skips it. See
 * `SpaceMembershipChange` for why that fact is spelled as its own variant rather than an empty
 * list: an empty list is what "unknown" degrades into by accident, and this helper's whole job on
 * an unknown outcome is to sweep MORE, not less.
 *
 * Invalidation-only by design, like the callers: any optimistic flip/rollback stays with them.
 * A caller holding an optimistic write on `communityDetailKey()` must protect it from the
 * refetch this helper triggers itself (see `communityDetailCache`'s hold) — this helper cannot
 * know another mutation is in flight.
 */
export function invalidateSpaceMembershipQueries(
  queryClient: QueryClient,
  { change, alreadySweptByCaller }: SpaceMembershipSweep,
): void {
  // The one input that proves nothing moved. Every other variant leaves open that the viewer's
  // membership changed somewhere.
  if (change.kind === 'none') return;

  // Per-space keys take the tightest set available: the spaces that provably changed, or (when
  // that is unknowable) the ones the caller could name. An unknowable outcome with no named spaces
  // sweeps no roster at all rather than widening to `spaceMembersBaseKey()` — every mounted roster
  // in the app is a steep price for a guess. The length-2 `spaceMembersKey` prefix covers every
  // status-filtered list, PENDING rows included.
  // Division of labour with SSE: useRealtimeInvalidation sweeps the same key on the
  // SPACE_MEMBER_JOINED echo. This local sweep is the path that survives SSE being down or
  // replaying, so neither side is redundant.
  //
  // De-duped defensively, matching `invalidateGoingEventQueries`, which dedupes its own id list at
  // the top of the function for the same reason: `executePostAuthIntent` builds both lists by
  // concatenating the attend response's buckets with no `Set` (`executeGoingEvent` in
  // executePostAuthIntent.ts), so a space in two buckets would reach a loop twice — the same
  // cancel-and-restart amplification this file exists to remove. Unreachable today, since
  // `AttendEventResponse`'s buckets are documented mutually exclusive
  // (src/models/api/event.ts:222-226); the invariant is simply not trusted at either boundary, and
  // every id loop in the pair defends. A duplicate is a wasted round-trip, not a no-op.
  for (const spaceId of new Set(change.kind === 'these' ? change.ids : change.candidateIds)) {
    void queryClient.invalidateQueries({ queryKey: spaceMembersKey(spaceId) });
    // The joined space's OWN event list. `list_space_events` runs `_apply_private_event_filter`,
    // whose predicate includes membership, so a join changes which events that tab may show: the
    // cached non-member subset is stale the moment the join lands, while My Calendars below
    // already lists those same events. `spaceEventsKey` is a standalone root — nothing else swept
    // here or by any caller's global roots reaches it — so it has to be named.
    if (!alreadySweptByCaller.spaceEventLists) {
      void queryClient.invalidateQueries({ queryKey: spaceEventsKey(spaceId) });
    }
    // The Partners page (TECH-86) is trimmed per viewer on the server: a visitor's copy has no
    // members-only row, only counts. A join, an approval or a leave changes which rows this viewer
    // may receive, so the person who joins from the partners gate lands back on the tab with the
    // hidden cards showing. A standalone root like `spaceEventsKey`, so it has to be named.
    void queryClient.invalidateQueries({ queryKey: spacePartnersKey(spaceId) });
  }

  // The global surfaces. All of them read membership rather than any one space's id: the
  // my-joined list and community list gain/lose a row, and every cached `CommunityRead` carries
  // `member_count` plus the viewer's `user_is_member` / `user_role` / `user_membership_status`.
  void queryClient.invalidateQueries({ queryKey: spacesMyJoinedBaseKey() });
  void queryClient.invalidateQueries({ queryKey: communitiesKey() });
  void queryClient.invalidateQueries({ queryKey: communityDetailKey() });
  // My Calendars gains the joined space's events — but only the caller knows whether it already
  // reached this key through `eventsBaseKey()`. Sweeping it anyway would cancel that parent's
  // in-flight request and issue a second one for the same data.
  if (!alreadySweptByCaller.eventRoot) {
    void queryClient.invalidateQueries({ queryKey: myCalendarsBaseKey() });
  }
  if (!alreadySweptByCaller.mySpacesFeed) {
    void queryClient.invalidateQueries({ queryKey: questionMySpacesFeedKey() });
  }
  // Held posts: the viewer's posts addressed to a Space change status with the viewer's
  // membership row (published on approval; declined when the request is declined, blocked or
  // withdrawn, or the member leaves or is removed), and the server sends no question-scoped
  // event for the decline. The status shows on the post page (slug or uuid route) and the
  // own-profile list, so all three go stale with the row. Here, in the single definition, and not
  // only on the SSE echo: the device that caused the change is told nothing by the server and
  // must refresh from its own settle. Not gated on what is cached: "is a held post cached for
  // this Space" cannot be answered reliably from the entity store, and only mounted queries
  // refetch. The two post-page roots are skipped only for a caller that already sweeps the post
  // pages and cannot have moved a held post (`heldPostPages`).
  if (!alreadySweptByCaller.heldPostPages) {
    void queryClient.invalidateQueries({ queryKey: resolveQuestionBaseKey() });
    void queryClient.invalidateQueries({ queryKey: questionByUuidBaseKey() });
  }
  void queryClient.invalidateQueries({ queryKey: userQuestionsBaseKey() });
}
